"use client";

import { type RefObject, useEffect, useState } from "react";

/**
 * Detect whether a line-clamped element is hiding overflow.
 *
 * `scrollHeight > clientHeight` alone is unreliable with `-webkit-line-clamp`
 * (especially WebKit / iOS / Capacitor): both values often report the clamped
 * box, so overflow is never detected and "Read more" never appears. Compare
 * the clamped box to an unclamped probe at the same width instead.
 */
export function isLineClampOverflow(el: HTMLElement): boolean {
  const width = el.getBoundingClientRect().width;
  const clampedHeight = el.clientHeight;
  if (width < 1 || clampedHeight < 1) return false;

  // Fast path — works in engines that report full scrollHeight under clamp.
  if (el.scrollHeight - clampedHeight > 1) return true;

  const styles = window.getComputedStyle(el);
  const probe = document.createElement("div");
  probe.setAttribute("aria-hidden", "true");
  probe.textContent = el.textContent ?? "";
  probe.style.cssText = [
    "position:fixed",
    "left:-99999px",
    "top:0",
    "visibility:hidden",
    "pointer-events:none",
    `width:${width}px`,
    "height:auto",
    "max-height:none",
    "overflow:visible",
    "display:block",
    "-webkit-line-clamp:unset",
    "-webkit-box-orient:initial",
    `font:${styles.font}`,
    `font-size:${styles.fontSize}`,
    `font-weight:${styles.fontWeight}`,
    `font-family:${styles.fontFamily}`,
    `font-style:${styles.fontStyle}`,
    `letter-spacing:${styles.letterSpacing}`,
    `line-height:${styles.lineHeight}`,
    `word-break:${styles.wordBreak}`,
    `overflow-wrap:${styles.overflowWrap}`,
    `white-space:${styles.whiteSpace}`,
    "padding:0",
    "margin:0",
    "border:0",
  ].join(";");

  document.body.appendChild(probe);
  const fullHeight = probe.scrollHeight;
  probe.remove();

  return fullHeight - clampedHeight > 1;
}

/**
 * Whether the referenced line-clamped element is currently truncating `text`.
 *
 * The clamped box keeps a fixed height, so a single post-mount read can miss
 * overflow if layout, CSS, or webfonts haven't settled yet — and its own box
 * size won't change to re-trigger an observer. Re-measures on several
 * independent signals: now, short timeouts, container resize, and font load.
 */
export function useIsClampedOverflow<T extends HTMLElement>(
  ref: RefObject<T | null>,
  text: string,
): boolean {
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const el = ref.current;
      if (!el) return;
      if ((el.textContent ?? "") !== text) return;
      setIsTruncated(isLineClampOverflow(el));
    };
    measure();
    const timers = [
      setTimeout(measure, 50),
      setTimeout(measure, 300),
      setTimeout(measure, 800),
    ];
    const observer = new ResizeObserver(measure);
    const el = ref.current;
    if (el) {
      observer.observe(el);
      if (el.parentElement) observer.observe(el.parentElement);
    }
    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }
    return () => {
      cancelled = true;
      for (const timer of timers) clearTimeout(timer);
      observer.disconnect();
    };
  }, [ref, text]);

  return isTruncated;
}
